/**
 * Whether an attempt's working is marked, per kind of attempt and kind of
 * question, when neither its paper nor its assignment says. Under "not marked"
 * the analysis does not tell a student with a right answer how their working
 * reads — only an error the answer survived, or time it cost.
 *
 * The editor of the `working` tenant setting, framed by TenantSettingsTab. The
 * setting is overridden whole: saving sets all four cells for this tenant.
 * See docs/FeatureDesigns/diagnosis/accuracy/working-role-target.md §2.
 */
import { useState } from 'react';
import { Button, Select } from '@shared-ui/components';
import { WORKING_DEFAULT, type WorkingByClass, type WorkingDefaults, type WorkingSetting } from '@padcanvas/shared';
import type { SettingEditorProps } from './TenantSettingsTab';

type Modality = keyof WorkingDefaults['byModality'];

const MODALITIES: Array<{ key: Modality; label: string }> = [
    { key: 'test', label: 'Test' },
    { key: 'practice', label: 'Assignment' },
];

const CLASSES: Array<{ key: keyof WorkingByClass; label: string; title: string }> = [
    { key: 'freeform', label: 'Free-form', title: 'Free-form and structured questions' },
    { key: 'other', label: 'Other', title: 'MCQ, numerical and single-line questions' },
];

/** A modality the stored value leaves out reads as the code default's. */
function cell(v: WorkingDefaults, m: Modality): WorkingByClass {
    return v.byModality[m] ?? WORKING_DEFAULT.byModality[m];
}

function same(a: WorkingDefaults, b: WorkingDefaults): boolean {
    return MODALITIES.every(({ key }) => CLASSES.every(c => cell(a, key)[c.key] === cell(b, key)[c.key]));
}

export function WorkingEditor({ value, save, saving, actions }: SettingEditorProps<'working'>) {
    const [draft, setDraft] = useState<WorkingDefaults>({
        byModality: { test: cell(value, 'test'), practice: cell(value, 'practice') },
    });

    const set = (m: Modality, c: keyof WorkingByClass, next: WorkingSetting) =>
        setDraft(d => ({ byModality: { ...d.byModality, [m]: { ...cell(d, m), [c]: next } } }));

    const dirty = !same(value, draft);

    return (
        <div className="tenants-form">
            <table className="tenant-working-matrix">
                <thead>
                    <tr>
                        <th />
                        {CLASSES.map(c => <th key={c.key} title={c.title}>{c.label}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {MODALITIES.map(m => (
                        <tr key={m.key}>
                            <th scope="row">{m.label}</th>
                            {CLASSES.map(c => (
                                <td key={c.key}>
                                    <Select
                                        aria-label={`${m.label}, ${c.label}`}
                                        value={cell(draft, m.key)[c.key]}
                                        onChange={e => set(m.key, c.key, e.target.value as WorkingSetting)}
                                    >
                                        <option value="marked">Marked</option>
                                        <option value="scratch">Not marked</option>
                                    </Select>
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="tenant-detail-save">
                {dirty && <span className="tenant-detail-note">Unsaved changes</span>}
                {actions}
                <Button onClick={() => save(draft)} loading={saving} disabled={!dirty}>Save</Button>
            </div>
        </div>
    );
}
